/**
 * Route Policy — kategori bazlı erişim kuralları (SSoT)
 * ======================================================
 * AuthGate state machine'in kararını verirken pathname'i 4 kategoriye
 * sınıflandırır. Eski Layout'taki `publicAndAuthPaths[]` array'i bu
 * dosyaya taşındı + genişletildi (auth-route ve bootstrapping ayrımı).
 *
 * Karar matrisi (state × kategori) `<AuthGate>` içinde.
 */

export type RouteCategory =
  /** Anonim + giriş yapmış kullanıcı için public içerik (landing, blog, public araçlar) */
  | 'public'
  /** Auth flow sayfaları — login'de READY ise dashboard'a redirect olur */
  | 'auth-route'
  /** Profil tamamlama / hata sayfaları — NEEDS_PROFILE state'inde render OK */
  | 'bootstrapping'
  /** Protected app (workspace, modüller) — sadece READY iken render */
  | 'app';

// ─── Sabit listeler ──────────────────────────────────────────────────────────

const PUBLIC_PATHS = new Set<string>([
  '/',
  '/e',                    // Hesapsız uzaktan eğitim portalı giriş (kısa kod) — alt yollar /e/ prefix'te
  '/t',                    // Hesapsız taşeron evrak portalı giriş (kısa kod) — alt yollar /t/ prefix'te
  '/ozellikler',
  '/fiyatlandirma',
  '/hakkimizda',
  '/iletisim',
  '/sss',
  '/isg-yazilimi',
  '/iso-45001-yazilimi',         // Public: SEO/GEO pillar — 33 modül × ISO madde matrisi
  '/ai-destekli-risk-analizi',   // Public: SEO/GEO landing — hibrit AI + defense in depth
  '/fotograftan-tehlike-tespiti',// Public: computer vision fotoğraf→tehlike landing (GEO Probe-2)
  '/ekipman-periyodik-kontrol-yazilimi', // Public: SEO/GEO landing — iş ekipmanları periyodik kontrol (EK-III + AI tespit + karekod)
  '/acil-durum-plani-yazilimi',   // Public: SEO/GEO modül-detay — acil durum eylem planı (Resmî Gazete 28681, AI taslak + tatbikat) [FAZ 3 golden]
  '/yillik-calisma-plani-yazilimi',   // Public: SEO/GEO modül-detay — yıllık çalışma planı (İş Güvenliği Uzmanları Yön. Md 9, şablon-driven) [FAZ 3, yillik-planlar primary]
  '/yillik-egitim-plani-yazilimi',   // Public: SEO/GEO modül-detay — yıllık eğitim planı (Resmî Gazete 33212 2 Nisan 2026, saat kotası) [FAZ 3, yillik-planlar alt-yaprak]
  '/isg-egitim-sunumu-yazilimi',   // Public: SEO/GEO modül-detay — İSG eğitim sunumu (AI slayt+test, Resmî Gazete 33212, ISO 7.2/7.3) [FAZ 3]
  '/uygunsuzluk-yonetimi-yazilimi', // Public: SEO/GEO modül-detay — Uygunsuzluk Yönetimi & DÖF (AI 5-Whys, ISO 45001 Md 10.2) [FAZ 3]
  '/is-kazasi-bildirim-yazilimi',   // Public: SEO/GEO modül-detay — İş Kazası Kayıt & Bildirim (AI RCA 3-metot, SGK takibi, 6331 Md 14) [FAZ 3]
  '/isg-egitim-kayitlari-yazilimi', // Public: SEO/GEO modül-detay — İSG Eğitim Kayıtları (Resmî Gazete 33212 uyum, EK-2 belge, uzaktan izleme) [FAZ 3]
  '/isg-kurulu-yazilimi',           // Public: SEO/GEO modül-detay — İSG Kurulu (hub-toplantı, 6331 Md 22, periyot+yeter-sayı) [FAZ 3]
  '/calisan-yonetimi-yazilimi',     // Public: SEO/GEO modül-detay — Çalışan Yönetimi (AI Excel/görsel import, 360° kart, KVKK) [FAZ 3]
  '/taseron-evrak-takip-yazilimi', // Public: SEO/GEO modül-detay — Taşeron evrak takibi (Resmî Gazete 33212 Md 22)
  '/is-izni-yazilimi', // Public: SEO/GEO modül-detay — İş izni (çalışma izni)
  '/calisan-temsilcisi-secimi-yazilimi', // Public: SEO/GEO modül-detay — Çalışan Temsilcisi Seçimi (6331 Md 20 + Resmî Gazete 28750)
  '/onlem-takip-yazilimi',          // Public: SEO/GEO modül-detay — Önlem Takibi (RA kontrol önlemleri, hiyerarşi Md 8.1.2, AI yok) [FAZ 3]
  '/acil-durum-tatbikati-yazilimi', // Public: SEO/GEO modül-detay — Acil Durum Tatbikatı (AI senaryo+değerlendirme, Resmî Gazete 28681 Md 13) [FAZ 3]
  '/kacis-plani-yazilimi',          // Public: SEO/GEO modül-detay — Kaçış Planı / Kat Planları (ISO 23601, ISO 7010, AI yok) [FAZ 3]
  '/onayli-defter-yazilimi',        // Public: SEO/GEO modül-detay — Onaylı Defter (İSG Hizmetleri Yön. Resmî Gazete 28512 Md 7, AI agregasyon, İSG-KATİP değil) [FAZ 3]
  '/yonetim-gozden-gecirme-yazilimi', // Public: SEO/GEO modül-detay — YGG (hub-toplantı, ISO 45001 Md 9.3, AI yok) [FAZ 3]
  '/calisan-katilimi-proseduru-yazilimi', // Public: SEO/GEO modül-detay — Çalışan Katılımı Prosedürü (45001 tek-doküman, Md 5.4) [FAZ 3]
  '/baglam-analizi-yazilimi',       // Public: SEO/GEO modül-detay — Bağlam Analizi (45001 tek-doküman, Md 4.1/4.2) [FAZ 3]
  '/kapsam-beyani-yazilimi',        // Public: SEO/GEO modül-detay — Kapsam Beyanı (45001 tek-doküman, Md 4.3) [FAZ 3]
  '/isg-politikasi-yazilimi',       // Public: SEO/GEO modül-detay — İSG Politikası (45001 tek-doküman, Md 5.2) [FAZ 3]
  '/roller-yetki-matrisi-yazilimi', // Public: SEO/GEO modül-detay — Roller ve Yetki Matrisi (45001 tek-doküman, Md 5.3) [FAZ 3]
  '/liderlik-ve-taahhut-yazilimi',  // Public: SEO/GEO modül-detay — Liderlik ve Taahhüt (45001 tek-doküman, Md 5.1) [FAZ 3]
  '/iletisim-plani-yazilimi',       // Public: SEO/GEO modül-detay — İSG İletişim Planı (45001 tek-doküman, Md 7.4) [FAZ 3]
  '/degisiklik-yonetimi-yazilimi',  // Public: SEO/GEO modül-detay — Değişiklik Yönetimi (45001 tek-doküman, Md 8.1.3) [FAZ 3]
  '/yuklenici-yonetimi-yazilimi',   // Public: SEO/GEO modül-detay — Yüklenici Yönetimi (45001 tek-doküman, Md 8.1.4) [FAZ 3]
  '/ic-denetim-yazilimi',           // Public: SEO/GEO modül-detay — İç Denetim (45001 tek-doküman, Md 9.2, STATİK) [FAZ 3]
  '/uyum-degerlendirme-yazilimi',   // Public: SEO/GEO modül-detay — Uygunluk Değerlendirme (45001 tek-doküman, Md 9.1.2, STATİK) [FAZ 3]
  '/yetkinlik-matrisi-yazilimi',    // Public: SEO/GEO modül-detay — Yetkinlik Matrisi (45001 DASHBOARD, Md 7.2) [FAZ 3]
  '/dokuman-merkezi-yazilimi',      // Public: SEO/GEO modül-detay — İSG Doküman Merkezi (45001 DASHBOARD/hub, Md 7.5) [FAZ 3]
  '/surekli-iyilestirme-yazilimi',  // Public: SEO/GEO modül-detay — Sürekli İyileştirme KPI (45001 DASHBOARD, Md 10.3) [FAZ 3]
  '/ai-vision',                  // Public: 3-Çekirdek AĞAÇ dalı — görsel yapay zeka çekirdeği (GÖRÜR)
  '/ai-mevzuat',                 // Public: 3-Çekirdek AĞAÇ dalı — mevzuat-temelli üretken çekirdek (ÜRETİR)
  '/vr-acil-durum-tatbikati',    // Public: öncü — VR/MR acil durum tatbikat platformu (erken erişim)
  '/ai-gozluk-risk-analizi',     // Public: öncü — AI (akıllı kamera) gözlük ile saha risk analizi (yol haritası/erken erişim)
  '/ai-gozluk-saha-gozlem',      // Public: öncü — akıllı gözlük ile saha gözlem (kardeş sayfa; çoklu bulgu → düzeltici faaliyet)
  '/dijital-atolye',             // Public: "İSG'nin Dijital Atölyesi" konsept + hub (Görsel Yapay Zeka çekirdek + VR/akıllı gözlük/fotoğraf-AI); nav destinasyonu
  '/uzaktan-isg-egitimi',        // Public: online eğitim landing — AI sunum + QR dağıtım + EK-2 katılım kanıtı
  '/tek-uzman-icin',             // Public: persona landing — bireysel İSG uzmanı (5-20 firma)
  '/ofismux',                    // Public: ofismux ürün lansman sayfası (brainmux ürünü, issue 001)
  '/isg-katip-eklentisi',        // Public: Chrome eklentisi tanıtım/kurulum landing'i (/isg-katip auth-gated kılavuz, banner buraya gider)
  '/guvenlik',
  '/araclar',              // Public: araç hub sayfası (alt sayfaları zaten PUBLIC_PREFIXES'te /araclar/)
  '/ornekler',             // Public: Örnek Kütüphanesi master hub (alt hub'lar PUBLIC_PREFIXES'te /<modul>/ornekler)
  '/risk-metodolojileri',  // Public: 12 risk değerlendirme metodu içerik sayfası
  '/surum-notlari',        // Public: ürün sürüm geçmişi (landing footer linki)
  '/hakkinda',             // Public: mobil app "Hakkında/Sürüm" kutusu (Play/PWA kısayolu; firma gerekmez)
  '/sorumluluk-reddi',
  '/kullanim-kosullari',
  '/gizlilik-politikasi',
  '/kvkk-aydinlatma-metni',
  '/cerez-politikasi',
  '/mesafeli-satis-sozlesmesi',
  '/teslimat-iade-sartlari',
  '/kayit-talep-alindi',  // Kayıt isteği SUCCESS sonrası teşekkür sayfası (anonim user buraya yönlenir)
  '/odeme-sonuc',  // iyzico callback redirect hedefi — AuthGate loading overlay'i sayfayı boğmasın.
                   // Token URL'de ödeme tanımlıyor. Sayfa kendi /checkout/result POST'unda
                   // 401 alırsa "Hesabınıza giriş yapın" mesajı + login button gösterir.
  '/on-bilgilendirme',      // Yasal: 6502 sayılı Kanun Md.5 ödeme öncesi ön bilgilendirme formu
  '/feed.xml',              // RSS feed (SSR getServerSideProps)
  '/sitemap.xml',           // Sitemap (SSR getServerSideProps)
  '/llms.txt',              // AI crawler metadata (SSR getServerSideProps)
  '/extension/hosgeldiniz'  // Chrome extension hoş geldin sayfası (no auth)
]);

/** Prefix bazlı public path'ler (örn. /blog/* veya /araclar/*) */
const PUBLIC_PREFIXES: readonly string[] = [
  '/araclar/',
  '/blog',
  '/fiyatlandirma/',  // /fiyatlandirma/karsilastir + gelecek alt sayfalar (kurumsal, sss vb.)
  '/risk-analizi/ornekler',
  '/saha-gozlem/ornekler',  // SEO sektör örnek hub + alt sayfaları (Sprint SEO-2 backlog)
  '/yillik-calisma-plani/ornekler',  // SEO yıllık çalışma planı örnek hub + 15 detay sayfa
  '/yillik-egitim-plani/ornekler',  // SEO yıllık eğitim planı örnek hub + 15 detay sayfa
  '/egitim-sunum/ornekler',         // SEO eğitim sunum örnek hub + 16 detay sayfa. SADECE bu prefix public — /egitim-sunum (login arkası app) korunur.
  '/acil-durum-plani/ornekler',     // SEO ADP örnek hub + detay. SADECE bu prefix public — /acil-durum-planlari (login arkası app, FARKLI path) korunur.
  '/tatbikat-plani/ornekler',       // SEO tatbikat örnek hub + 30 detay. SADECE bu prefix public.
  '/formlar/ornekler',              // SEO İSG form örnek hub + detay. SADECE bu prefix public (/isg-sablonlari auth app, FARKLI kök).
  '/talimatlar/ornekler',           // SEO İSG talimat örnek hub + detay. SADECE bu prefix public.
  '/e/',                        // Hesapsız çalışan uzaktan eğitim portalı (/e/{token}, /e/egitim/{id}) — kendi scoped-JWT'si. NOT: trailing slash ŞART (/e değil) → /egitim-* yanlış-eşleşmez.
  '/t/',                        // Hesapsız taşeron evrak portalı (/t/{token}) — kendi tokeni (x-taseron-token). NOT: trailing slash ŞART → /taseronlar (auth app) YANLIŞ-eşleşmez.
  '/izin/',                     // İş izni karekod saha durum sayfası (/izin/{token}) — token-gated. NOT: trailing slash ŞART → /is-izinleri (auth app) eşleşmez.
  '/ekipman/'                   // Ekipman karekod public raporu (/ekipman/{token}) — token-gated. NOT: trailing slash ŞART → auth-gated /ekipman-kontrol YANLIŞ-eşleşmez.
];

const AUTH_ROUTES = new Set<string>([
  '/auth/login',
  '/auth/register',
  '/auth/callback',
  '/auth/reset-password',
  '/auth/update-password',
  '/auth/onaylandi'
]);

const BOOTSTRAPPING_ROUTES = new Set<string>([
  '/profil/kayit-tamamla',
  '/hata/kayit-engellendi',
  '/account-suspended',
  '/account-required',
  // Eklenti bağlama (OAuth-benzeri handshake): AuthGate burada redirect ATMAMALI —
  // connect.tsx kendi guard'ıyla TEK redirect mercii olur ve ?next='e state +
  // redirect_uri query'lerini TAM korur. Aksi halde AuthGate 'app' redirect'i
  // router.pathname (query'siz) kullanıp query'yi siliyor → ilk-deneme bağlanma
  // başarısız oluyordu (2. denemede çalışıyordu).
  '/extension/connect'
]);

// ─── Classifier ──────────────────────────────────────────────────────────────

export function classifyRoute(pathname: string): RouteCategory {
  // Exact match önce
  if (BOOTSTRAPPING_ROUTES.has(pathname)) return 'bootstrapping';
  if (AUTH_ROUTES.has(pathname)) return 'auth-route';
  if (PUBLIC_PATHS.has(pathname)) return 'public';

  // Prefix match
  for (const prefix of PUBLIC_PREFIXES) {
    if (pathname.startsWith(prefix)) return 'public';
  }

  // Fallback: protected app
  return 'app';
}

// ─── Convenience predicates ──────────────────────────────────────────────────

export function isPublicRoute(pathname: string): boolean {
  return classifyRoute(pathname) === 'public';
}

export function isAuthRoute(pathname: string): boolean {
  return classifyRoute(pathname) === 'auth-route';
}

export function isBootstrappingRoute(pathname: string): boolean {
  return classifyRoute(pathname) === 'bootstrapping';
}

export function isAppRoute(pathname: string): boolean {
  return classifyRoute(pathname) === 'app';
}

/** Public + auth + bootstrapping (yani "session olmadan da OK" path'ler) */
export function isAnonAccessibleRoute(pathname: string): boolean {
  const c = classifyRoute(pathname);
  return c === 'public' || c === 'auth-route' || c === 'bootstrapping';
}

/**
 * Güvenli post-login redirect path kontrolü (open redirect koruması).
 *
 * Kabul edilir:
 *   - "/dashboard", "/risk-analizleri/abc-123"
 * Red:
 *   - "//evil.com" (protocol-relative)
 *   - "/../etc" (directory traversal)
 *   - "http://..." (absolute external)
 *   - "javascript:..." veya kontrol karakterleri içeren
 *
 * `?next=` query param consume eden her component bunu ÖNCE çağırmalı.
 */
export function isSafeNextPath(next: unknown): next is string {
  if (typeof next !== 'string' || next.length === 0 || next.length > 500) return false;
  if (!next.startsWith('/')) return false;
  if (next.startsWith('//')) return false; // protocol-relative
  if (next.includes('..')) return false; // directory traversal
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f]/.test(next)) return false; // control chars
  if (next.toLowerCase().startsWith('/javascript:')) return false; // pseudo-scheme
  return true;
}
